/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import React, { useCallback, useState } from 'react';
import { InputColumn, TableInputProps } from './InputInterface';
import { TableInputForm } from './TableInputForm';
import { isColumnVisible, recalculateRowColumns, recalculateVisibilityRules, VisibilityResult } from './calculation';
import { isDisabled, updateRowData } from './tableUtils';
import { Button, Collapse } from 'reactstrap';
import TableInputView from '../../view/TableInputView';
import { RowDataInfo, RowStatus } from '../../../question/questionInterfaces';
import { TableErrorModal } from './partial/TableErrorModal';
import './MultiRowTableInput.scss';
import { generateDecimalErrorMessage } from '../../../utils/input';

const commonBtnClasses = 'w-100 rounded-0 d-flex justify-content-center align-items-center';

export default function MultiRowTableInput(props: TableInputProps) {
  const {
    table,
    updateTable,
    tableConfiguration,
    calculationColumns,
    visibilityRuleColumns,
    scrollToRef,
    universalTracker,
  } = props;
  const disabled = props.isDisabled?.();
  const { rows: rowDataInfo, editRowId } = table;

  const [formRow, setFormRowData] = React.useState<InputColumn[]>([]);
  const [visibilityResults, setVisibilityResults] = React.useState<VisibilityResult>();
  const [showEmptyTableErrorModal, setShowEmptyTableErrorModal] = useState<boolean>(false);

  const [isAdding, setIsAdding] = useState(true);

  const setFormRow = useCallback((data: InputColumn[]) => {
    const { visibleColumns: row, visibleResults: visibilityResults } = recalculateVisibilityRules(data, visibilityRuleColumns, {})
    setVisibilityResults(visibilityResults)
    setFormRowData(
      row
    );
  }, [visibilityRuleColumns]);

  // Initialize columns validation
  React.useEffect(() => {
    setFormRow(
      tableConfiguration.columns.map((c) => ({ code: c.code }))
    );
  }, [tableConfiguration, setFormRow]);

  const setRowData = (rows: RowDataInfo[]) => updateTable({ rows });
  const setRowEditId = (id: number | string) => updateTable({ editRowId: Number(id) });

  const resetEditRow = () =>
    updateTable({
      rows: updateRowData(rowDataInfo, { id: editRowId, isEdited: false }),
      editRowId: -1,
    });

  const clearInputRow = () => {
    setFormRow(tableConfiguration.columns.map((c) => ({ code: c.code })));
  };

  const clearData = () => {
    resetEditRow();
    clearInputRow();
    setIsAdding(false);
  };

  const onAdd = () => {
    const newRow = {
      rowStatus: RowStatus.added,
      id: rowDataInfo.length,
      data: [...formRow],
    };
    updateTable({ rows: [...rowDataInfo, newRow], editRowId: -1 });
    clearInputRow();
    setIsAdding(false);
  };

  function onEdit() {
    clearInputRow();
    updateTable({
      rows: updateRowData(rowDataInfo, {
        id: editRowId,
        data: formRow,
        hasChanged: true,
        isEdited: false,
      }),
      editRowId: -1,
    });
  }

  const inputMessage = generateDecimalErrorMessage({
    utr: universalTracker,
    input: Object.fromEntries(formRow.map((k) => [[k.code], k.value])),
  });

  const addBtnDisabled =
    isDisabled(tableConfiguration.columns, formRow) ||
    (!!inputMessage && Object.keys(inputMessage).some((k) => inputMessage[k]));

  const handleClearDeleteRow = (row: RowDataInfo) => {
    setRowData(
      updateRowData(rowDataInfo, {
        id: row.id,
        isRemoved: false,
      })
    );
  };

  const tableInputViewProps = {
    ...props,
    editRowId,
    disabled,
    rowData: rowDataInfo,
    handleEditRow: (row: RowDataInfo) => {
      setIsAdding(true)
      setFormRow([...row.data]);
      setRowData(
        updateRowData(rowDataInfo, {
          id: row.id,
          isRemoved: false,
        })
      );
      setRowEditId(row.id);
      scrollToRef();
    },
    handleCopyRow: (row: RowDataInfo) => {
      setIsAdding(true)
      resetEditRow();
      setFormRow([...row.data]);
      scrollToRef();
    },
    handleDeleteRow: (row: RowDataInfo) => {
      clearData();
      if (row.rowStatus === RowStatus.added) {
        return setRowData(rowDataInfo.filter(({ id }) => row.id !== id));
      }
      setRowData(updateRowData(rowDataInfo, { id: row.id, isRemoved: true }));
      const remainingRows = rowDataInfo.filter((r) => !r.isRemoved && r.id !== row.id);
      if (remainingRows.length === 0) {
        setShowEmptyTableErrorModal(true);
      }
    },
    handleClearDeleteRow,
  };

  const mergedProps = {
    ...props,
    inputMessage,
    tableConfiguration,
    calculationColumns,
    multiRow: true,
    // override internal
    row: formRow,
    isColumnVisible: (code: string) => isColumnVisible(code, visibilityResults),
    updateColumn: (column: InputColumn) => {
      setFormRow(
        recalculateRowColumns(formRow, column, calculationColumns)
      );
    },
  };

  return (
    <div className='table-input multi-row'>
      <TableInputView {...tableInputViewProps} />
      <Collapse isOpen={!disabled && isAdding}>
        <div className='table-input__form-container'>
          <i onClick={clearData} className='fa-solid fa-xmark table-input__close-btn'></i>
          <TableInputForm {...mergedProps} />
        </div>
        {mergedProps.isDisabled?.() ? (
          <></>
        ) : (
          <>
            {editRowId === -1 ? (
              <Button
                color='primary'
                className={`${commonBtnClasses} border-0 table-input__save-btn`}
                disabled={addBtnDisabled}
                onClick={onAdd}
              >
                <i className='fa fa-plus mr-2' />
                Save to table
              </Button>
            ) : (
              <Button
                color='primary'
                className={`${commonBtnClasses} border-0 table-input__save-btn`}
                disabled={addBtnDisabled}
                onClick={onEdit}
              >
                <i className='fa fa-plus mr-2' />
                Update row
              </Button>
            )}
            <Button outline onClick={clearData} className={`${commonBtnClasses} table-input__cancel-btn`}>
              <i className='fa-solid fa-angle-up mr-2'></i>
              Cancel
            </Button>
          </>
        )}
      </Collapse>
      {!isAdding && (
        <Button outline onClick={() => setIsAdding(true)} className={`${commonBtnClasses} table-input__add-btn`}>
          <i className='fas fa-plus fa-2x mr-2' />
          Add row
        </Button>
      )}

      {showEmptyTableErrorModal ? (
        <TableErrorModal setShowEmptyTableErrorModal={setShowEmptyTableErrorModal} {...props} />
      ) : null}
    </div>
  );
}
